/**
 * Dataset-Specific Data Loading Hooks
 * React Query hooks for loading data from specific datasets for optimization
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useDataset } from '../contexts/DatasetContext';
import type { Vehicle, Job, Location } from '../types/vrp';

// =============================================
// DATASET-SPECIFIC DATA HOOKS
// =============================================

/**
 * Load vehicles for the current selected dataset
 * Uses dataset context to load specific data for optimization
 */
export const useDatasetVehicles = (projectId: number, options?: {
  enabled?: boolean;
  staleTime?: number;
}) => {
  const { currentDataset, isLoading: datasetLoading } = useDataset();
  
  return useQuery({
    queryKey: ['dataset-vehicles', currentDataset?.id, projectId],
    queryFn: async () => {
      if (!currentDataset?.id || !projectId) {
        console.warn('🚗 No dataset/project selected for vehicle loading');
        return [];
      }
      
      console.log('🚗 [useDatasetVehicles] Loading vehicles for dataset:', currentDataset.id, currentDataset.name);
      const result = await apiClient.getDatasetVehicles(currentDataset.id, projectId);
      
      // Handle response format
      if (result && result.success && Array.isArray(result.data)) {
        console.log('✅ [useDatasetVehicles] Loaded', result.data.length, 'vehicles from dataset');
        return result.data;
      }
      
      if (Array.isArray(result)) {
        console.log('✅ [useDatasetVehicles] Direct array response:', result.length, 'vehicles');
        return result;
      }
      
      console.warn('⚠️ [useDatasetVehicles] Unexpected response format, returning empty array');
      return [];
    },
    enabled: (options?.enabled !== false) && !!currentDataset?.id && !!projectId && !datasetLoading,
    staleTime: options?.staleTime || 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

/**
 * Load jobs for the current selected dataset
 * Uses dataset context to load specific data for optimization
 */
export const useDatasetJobs = (projectId: number, options?: {
  enabled?: boolean;
  staleTime?: number;
}) => {
  const { currentDataset, isLoading: datasetLoading } = useDataset();
  
  return useQuery({
    queryKey: ['dataset-jobs', currentDataset?.id, projectId],
    queryFn: async () => {
      if (!currentDataset?.id || !projectId) {
        console.warn('💼 No dataset/project selected for job loading');
        return [];
      }
      
      console.log('💼 [useDatasetJobs] Loading jobs for dataset:', currentDataset.id, currentDataset.name);
      const result = await apiClient.getDatasetJobs(currentDataset.id, projectId);
      
      // Handle response format
      if (result && result.success && Array.isArray(result.data)) {
        console.log('✅ [useDatasetJobs] Loaded', result.data.length, 'jobs from dataset');
        return result.data;
      }
      
      if (Array.isArray(result)) {
        console.log('✅ [useDatasetJobs] Direct array response:', result.length, 'jobs');
        return result;
      }
      
      console.warn('⚠️ [useDatasetJobs] Unexpected response format, returning empty array');
      return [];
    },
    enabled: (options?.enabled !== false) && !!currentDataset?.id && !!projectId && !datasetLoading,
    staleTime: options?.staleTime || 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

/**
 * Load locations for the current selected dataset
 * Uses dataset context to load specific data for optimization
 */
export const useDatasetLocations = (projectId: number, options?: {
  enabled?: boolean;
  staleTime?: number;
}) => {
  const { currentDataset, isLoading: datasetLoading } = useDataset();
  
  return useQuery({
    queryKey: ['dataset-locations', currentDataset?.id, projectId],
    queryFn: async () => {
      if (!currentDataset?.id || !projectId) {
        console.warn('📍 No dataset/project selected for location loading');
        return [];
      }
      
      console.log('📍 [useDatasetLocations] Loading locations for dataset:', currentDataset.id, currentDataset.name);
      const result = await apiClient.getDatasetLocations(currentDataset.id, projectId);
      
      // Handle response format
      if (result && result.success && Array.isArray(result.data)) {
        console.log('✅ [useDatasetLocations] Loaded', result.data.length, 'locations from dataset');
        return result.data;
      }
      
      if (Array.isArray(result)) {
        console.log('✅ [useDatasetLocations] Direct array response:', result.length, 'locations');
        return result;
      }
      
      console.warn('⚠️ [useDatasetLocations] Unexpected response format, returning empty array');
      return [];
    },
    enabled: (options?.enabled !== false) && !!currentDataset?.id && !!projectId && !datasetLoading,
    staleTime: options?.staleTime || 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

/**
 * Load complete dataset context information
 * Provides dataset, scenario, project, and statistics data
 */
export const useDatasetContext = (datasetId?: number, options?: {
  enabled?: boolean;
  staleTime?: number;
}) => {
  const { currentDataset } = useDataset();
  const targetDatasetId = datasetId || currentDataset?.id;
  
  return useQuery({
    queryKey: ['dataset-context', targetDatasetId],
    queryFn: async () => {
      if (!targetDatasetId) {
        throw new Error('No dataset ID provided for context loading');
      }
      
      console.log('📊 [useDatasetContext] Loading complete context for dataset:', targetDatasetId);
      const result = await apiClient.getDatasetContext(targetDatasetId);
      
      console.log('✅ [useDatasetContext] Loaded dataset context:', {
        dataset: result.dataset?.name,
        scenario: result.scenario?.name,
        project: result.project?.name,
        has_statistics: !!result.statistics
      });
      
      return result;
    },
    enabled: (options?.enabled !== false) && !!targetDatasetId,
    staleTime: options?.staleTime || 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
};

/**
 * Combined hook for all dataset-specific data needed for optimization
 * Loads vehicles, jobs, and locations for the current dataset in parallel
 */
export const useDatasetOptimizationData = (projectId: number, options?: {
  enabled?: boolean;
  staleTime?: number;
}) => {
  const { currentDataset, statistics } = useDataset();
  
  const vehiclesQuery = useDatasetVehicles(projectId, options);
  const jobsQuery = useDatasetJobs(projectId, options);
  const locationsQuery = useDatasetLocations(projectId, options);
  
  const isLoading = vehiclesQuery.isLoading || jobsQuery.isLoading || locationsQuery.isLoading;
  const hasError = vehiclesQuery.error || jobsQuery.error || locationsQuery.error;
  
  // Data completeness validation
  const isDataComplete = !!(
    vehiclesQuery.data && vehiclesQuery.data.length > 0 &&
    jobsQuery.data && jobsQuery.data.length > 0 &&
    locationsQuery.data && locationsQuery.data.length > 0
  );
  
  const dataValidation = {
    hasVehicles: !!(vehiclesQuery.data && vehiclesQuery.data.length > 0),
    hasJobs: !!(jobsQuery.data && jobsQuery.data.length > 0),
    hasLocations: !!(locationsQuery.data && locationsQuery.data.length > 0),
    vehicleCount: vehiclesQuery.data?.length || 0,
    jobCount: jobsQuery.data?.length || 0,
    locationCount: locationsQuery.data?.length || 0,
    isComplete: isDataComplete,
    missingData: [
      ...((!vehiclesQuery.data || vehiclesQuery.data.length === 0) ? ['vehicles'] : []),
      ...((!jobsQuery.data || jobsQuery.data.length === 0) ? ['jobs'] : []),
      ...((!locationsQuery.data || locationsQuery.data.length === 0) ? ['locations'] : [])
    ]
  };
  
  console.log('🎯 [useDatasetOptimizationData] Data validation for dataset:', currentDataset?.name, {
    ...dataValidation,
    hasStatistics: !!statistics
  });
  
  return {
    // Data
    vehicles: vehiclesQuery.data || [],
    jobs: jobsQuery.data || [],
    locations: locationsQuery.data || [],
    statistics,
    
    // Loading states
    isLoading,
    isVehiclesLoading: vehiclesQuery.isLoading,
    isJobsLoading: jobsQuery.isLoading,
    isLocationsLoading: locationsQuery.isLoading,
    
    // Error states
    error: hasError,
    vehiclesError: vehiclesQuery.error,
    jobsError: jobsQuery.error,
    locationsError: locationsQuery.error,
    
    // Validation
    validation: dataValidation,
    isDataComplete,
    
    // Dataset context
    currentDataset,
    
    // Refetch functions
    refetchAll: () => {
      vehiclesQuery.refetch();
      jobsQuery.refetch();
      locationsQuery.refetch();
    },
    refetchVehicles: vehiclesQuery.refetch,
    refetchJobs: jobsQuery.refetch,
    refetchLocations: locationsQuery.refetch
  };
};

// Export types
export type DatasetOptimizationData = ReturnType<typeof useDatasetOptimizationData>;